import { Button } from '@consigli/facade';
import { useServiceName } from '@consigli/hooks';
import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosInformationCircle, IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import Popup from 'reactjs-popup';

import { LegalWarning } from '@/components/legal-warning';

import { HelpTooltip } from './help-tooltip';

interface IFCHelpIconProps {
  setShowPopup: Dispatch<SetStateAction<boolean>>;
}

const IFCHelpIcon = ({ setShowPopup }: IFCHelpIconProps) => {
  const { t } = useTranslation();
  const onClose = () => setShowPopup(false);
  return (
    <Popup
      overlayStyle={{ background: 'rgba(100, 100, 100, 0.35)' }}
      contentStyle={{
        backgroundColor: '#edf2f7',
        borderRadius: '10px',
        width: '90%',
        maxHeight: '750px',
        maxWidth: '800px',
        overflow: 'auto',
      }}
      modal
      open
      onClose={onClose}
    >
      <div className="p-4">
        <div className="font-bold py-4 flex justify-center">{t('help-icon.ifc-file-input')}</div>
        <div className="mb-4">{t('help-icon.ifc-file-input-description')}</div>
        <>
          <h2 className="font-bold py-1">{t('help-icon.space')}</h2>
          <ul className="px-4">
            <li className="list-disc">{t('help-icon.space-description')}</li>
          </ul>
        </>
        <>
          <h2 className="font-bold py-1">{t('help-icon.slabs')}</h2>
          <ul className="px-4">
            <li className="list-disc">{t('help-icon.slabs-description')}</li>
          </ul>
        </>
        <>
          <h2 className="font-bold py-1">{t('help-icon.ceiling-coverings')}</h2>
          <ul className="px-4">
            <li className="list-disc">{t('help-icon.ceiling-coverings-description')}</li>
          </ul>
        </>
        <>
          <h2 className="font-bold py-1">{t('help-icon.walls')}</h2>
          <ul className="px-4">
            <li className="list-disc">{t('help-icon.walls-description')}</li>
          </ul>
        </>
        <>
          <h2 className="font-bold py-1">{t('help-icon.doors')}</h2>
          <ul className="px-4">
            <li className="list-disc">{t('help-icon.doors-description')}</li>
          </ul>
        </>
        <>
          <h2 className="font-bold py-1">{t('help-icon.windows')}</h2>
          <ul className="px-4">
            <li className="list-disc">{t('help-icon.windows-description')}</li>
          </ul>
        </>
        <>
          <h2 className="font-bold py-1">{t('help-icon.stairs')}</h2>
          <ul className="px-4">
            <li className="list-disc">{t('help-icon.stairs-description')}</li>
          </ul>
        </>
        <div className="flex justify-center">
          <Button secondary rounded onClick={onClose}>
            {t('help-icon.close')}
          </Button>
        </div>
      </div>
    </Popup>
  );
};

interface SpecificationIconProps {
  setShowSpecificationPopup: Dispatch<SetStateAction<boolean>>;
}

const SpecificationIcon = ({ setShowSpecificationPopup }: SpecificationIconProps) => {
  const { t } = useTranslation();
  const onClose = () => setShowSpecificationPopup(false);
  return (
    <Popup
      overlayStyle={{ background: 'rgba(100, 100, 100, 0.35)' }}
      contentStyle={{
        backgroundColor: '#edf2f7',
        borderRadius: '10px',
        width: '80%',
        maxWidth: '400px',
      }}
      modal
      open
      onClose={onClose}
    >
      <div className="p-4 relative overflow-auto">
        <>{t('help-icon.specification-description')}</>
        <div className="flex justify-center mt-4">
          <Button secondary rounded onClick={onClose}>
            {t('help-icon.close')}
          </Button>
        </div>
      </div>
    </Popup>
  );
};

interface UploadInfoPanelProps {
  /**
   * Optional content to show in the collapsible file types section.
   * If not provided, the collapsible section is hidden.
   */
  fileTypesContent?: ReactNode;
  /**
   * Optional label for the collapsible section button.
   * If not provided, only the info icon and arrow will be shown.
   */
  fileTypesLabel?: string;
  /**
   * Optional content to show at the bottom of the panel (always visible, not collapsible).
   */
  footerContent?: ReactNode;
}

/**
 * A panel that displays the legal warning (always shown) and optionally
 * a collapsible section for file type information.
 */
export const UploadInfoPanel = ({
  fileTypesContent,
  fileTypesLabel,
  footerContent,
}: UploadInfoPanelProps) => {
  const [showFileFormats, setShowFileFormats] = useState(false);

  return (
    <div className="bg-day-light-3 border border-day-light-1 rounded-lg p-4 w-full">
      {/* Legal Warning Section - Always Shown */}
      <LegalWarning className={fileTypesContent || footerContent ? 'mb-3' : ''} />

      {/* Collapsible File Formats Section - Only shown if fileTypesContent is provided */}
      {fileTypesContent && (
        <div className="border-t border-day-light-1 pt-3">
          <Button
            tertiary
            icon={IoIosInformationCircle}
            iconSize={20}
            iconColor="#7588a3"
            onClick={() => setShowFileFormats(!showFileFormats)}
            className="flex items-center gap-1 text-sm text-day-neutral-subtle hover:text-day-neutral-dark"
          >
            {fileTypesLabel && <span className="underline">{fileTypesLabel}</span>}
            {showFileFormats ? (
              <IoIosArrowUp size={16} className="ml-1" />
            ) : (
              <IoIosArrowDown size={16} className="ml-1" />
            )}
          </Button>
          {showFileFormats && <div className="mt-2 ml-6 text-sm">{fileTypesContent}</div>}
        </div>
      )}

      {/* Footer Content - Always visible */}
      {footerContent && (
        <div className="border-t border-day-light-1 pt-3 text-sm text-day-neutral-subtle">
          {footerContent}
        </div>
      )}
    </div>
  );
};

export const StructuralUploadInfo = () => {
  const { t } = useTranslation();
  const serviceName = useServiceName();

  return (
    <UploadInfoPanel
      fileTypesLabel={t('package-creation.filetype-info.necessary-files')}
      fileTypesContent={
        <p>&bull; {t(`package-creation.filetype-info.${serviceName}.first-filetype`)}</p>
      }
    />
  );
};

export const OptimizationUploadInfo = () => {
  const { t } = useTranslation();
  const serviceName = useServiceName();
  const [showIfcPopup, setShowIfcPopup] = useState(false);
  const [showSpecificationPopup, setShowSpecificationPopup] = useState(false);
  const secondFiletypeText = t(`package-creation.filetype-info.${serviceName}.second-filetype`);

  return (
    <>
      <UploadInfoPanel
        fileTypesLabel={t('package-creation.filetype-info.necessary-files')}
        fileTypesContent={
          <div className="space-y-1">
            <div className="flex items-center">
              <IoIosInformationCircle
                color="#7588a3"
                size={18}
                className="cursor-pointer mr-1 flex-shrink-0"
                onClick={() => setShowIfcPopup(true)}
              />
              <HelpTooltip id="help-tooltip-1" tooltip={t('help-icon.tooltip-text')} />
              <span>{t(`package-creation.filetype-info.${serviceName}.first-filetype`)}</span>
            </div>
            {secondFiletypeText && (
              <div className="flex items-center">
                <IoIosInformationCircle
                  color="#7588a3"
                  size={18}
                  className="cursor-pointer mr-1 flex-shrink-0"
                  onClick={() => setShowSpecificationPopup(true)}
                />
                <HelpTooltip id="help-tooltip-2" tooltip={t('help-icon.tooltip-text')} />
                <span>{t(`package-creation.filetype-info.${serviceName}.second-filetype`)}</span>
              </div>
            )}
          </div>
        }
      />
      {showIfcPopup && <IFCHelpIcon setShowPopup={setShowIfcPopup} />}
      {showSpecificationPopup && (
        <SpecificationIcon setShowSpecificationPopup={setShowSpecificationPopup} />
      )}
    </>
  );
};

export const InsightUploadInfo = () => {
  const { t } = useTranslation();

  return (
    <UploadInfoPanel
      footerContent={
        <>
          <p>{t('file-upload.split-documents-tip')}</p>
          <p>{t('file-upload.text-content-only')}</p>
        </>
      }
    />
  );
};

interface ServiceUploadInfoPanelProps {
  howItWorksBody: string;
}

const ServiceUploadInfoPanel = ({ howItWorksBody }: ServiceUploadInfoPanelProps) => {
  const { t } = useTranslation();

  return (
    <div className="bg-day-light-3 border border-day-light-1 rounded-lg p-4 w-full">
      <div className="mb-3 flex gap-2">
        <span className="mt-0.5 flex-shrink-0">
          <IoIosInformationCircle size={18} color="#7588a3" />
        </span>
        <div className="text-sm">
          <p>
            <strong>{t('tender-info.how-it-works-title')}</strong> {howItWorksBody}
          </p>
          <p className="mt-2">{t('tender-info.text-content-note')}</p>
        </div>
      </div>

      <div className="mb-3 flex gap-2">
        <span className="mt-0.5 flex-shrink-0">
          <IoIosInformationCircle size={18} color="#7588a3" />
        </span>
        <LegalWarning className="text-sm" />
      </div>

      <div className="flex gap-2">
        <span className="mt-0.5 flex-shrink-0">
          <IoIosInformationCircle size={18} color="#7588a3" />
        </span>
        <p className="text-sm">
          <strong>{t('tender-info.tip-title')}</strong> {t('tender-info.tip-body')}
        </p>
      </div>
    </div>
  );
};

export const TenderUploadInfo = () => {
  const { t } = useTranslation();
  return <ServiceUploadInfoPanel howItWorksBody={t('tender-info.how-it-works-body')} />;
};

export const DesignPackageReviewUploadInfo = () => {
  const { t } = useTranslation();
  return <ServiceUploadInfoPanel howItWorksBody={t('dpr-info.how-it-works-body')} />;
};

/**
 * Upload info panel for Load Calculation wizard - shows legal warning + load calculation file types
 */
export const LoadCalculationUploadInfo = () => {
  const { t } = useTranslation();
  const serviceName = useServiceName();

  return (
    <UploadInfoPanel
      fileTypesLabel={t('package-creation.filetype-info.necessary-files')}
      fileTypesContent={
        <>
          <p>&bull; {t(`package-creation.filetype-info.${serviceName}.first-filetype`)}</p>
          <p>&bull; {t(`package-creation.filetype-info.${serviceName}.second-filetype`)}</p>
        </>
      }
    />
  );
};

/**
 * Upload info panel for Schedule Check wizard - shows legal warning + schedule check instructions
 */
export const ScheduleCheckUploadInfo = () => {
  const { t } = useTranslation();

  return (
    <UploadInfoPanel
      fileTypesLabel={t('package-creation.schedule-check-title')}
      fileTypesContent={<p>{t('package-creation.schedule-check-instructions')}</p>}
    />
  );
};

/**
 * Upload info panel for Site Due Diligence wizard - shows legal warning
 */
export const SiteDueDiligenceUploadInfo = () => {
  return <UploadInfoPanel />;
};

/**
 * Upload info panel for Version Compare wizard - shows how it works, legal warning, and tip.
 */
export const VersionCompareUploadInfo = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-day-light-3 border border-day-light-1 rounded-lg p-4 w-full">
      <div className="mb-3 flex gap-2">
        <span className="mt-0.5 flex-shrink-0">
          <IoIosInformationCircle size={18} color="#7588a3" />
        </span>
        <div className="text-sm">
          <p>
            <strong>{t('version-compare-info.how-it-works-title')}</strong>{' '}
            {t('version-compare-info.how-it-works-body')}
          </p>
        </div>
      </div>

      <div className="mb-3 flex gap-2">
        <span className="mt-0.5 flex-shrink-0">
          <IoIosInformationCircle size={18} color="#7588a3" />
        </span>
        <LegalWarning className="text-sm" />
      </div>

      <div className="flex gap-2">
        <span className="mt-0.5 flex-shrink-0">
          <IoIosInformationCircle size={18} color="#7588a3" />
        </span>
        <p className="text-sm">
          <strong>{t('version-compare-info.tip-title')}</strong>{' '}
          {t('version-compare-info.tip-body')}
        </p>
      </div>
    </div>
  );
};

export const WaterProcessSystemsAgentUploadInfo = () => {
  const { t } = useTranslation();

  return (
    <UploadInfoPanel
      fileTypesLabel={t('package-creation.water-process-title')}
      fileTypesContent={<p>{t('package-creation.water-process-instructions')}</p>}
    />
  );
};
